import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, memo } from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { MdSort } from 'react-icons/md';
import { useAnalytics } from 'use-analytics';
import Dropdown from '../../Dropdown';
import Filter from '../Filter';
import { SortDirection } from '../../../constants';

const SortingFilter = ({
  sortingValues,
  initialValue,
  value,
  direction,
  onSortChange,
  resultsLabel,
  variant,
  widthInside,
  isSelectionOptionBlack,
  filterSpacingClass,
  fontSize,
  dropdownAligning,
}) => {
  const { track } = useAnalytics();
  const [selectedSortOptionLabel, setSelectedSortOptionLabel] = useState('');
  useEffect(() => {
    const optionItem = sortingValues.find((v) => v.value === value);
    if (optionItem) {
      setSelectedSortOptionLabel(optionItem.label);
      return;
    }
    setSelectedSortOptionLabel(sortingValues[0].label);
    const searchObj = {
      sortBy: sortingValues[0].value,
      sortDirection: SortDirection.ASCEND,
    };
    onSortChange(searchObj);
  }, [value, sortingValues, onSortChange]);

  const handleSortChange = useCallback(
    (option) => {
      const searchObj = {
        page: 1,
      };
      if (value === option.value) {
        searchObj.sortDirection = direction ? SortDirection.ASCEND : SortDirection.DESCEND;
      } else {
        searchObj.sortBy = option.value;
        searchObj.sortDirection = SortDirection.ASCEND;
      }
      onSortChange(searchObj);

      track('sortingFilter.handleSortChange', {
        resultsLabel,
        sortBy: option.label,
        sortDirection:
          searchObj.sortDirection === SortDirection.DESCEND ? 'descending' : 'ascending',
        previousSortBy: selectedSortOptionLabel,
      });
    },
    [direction, onSortChange, resultsLabel, selectedSortOptionLabel, track, value],
  );

  return (
    <Filter
      menuWidthClass="md:w-64"
      label={
        isSelectionOptionBlack
          ? `<span style="margin-right:7px">Sort By:</span><span style="font-weight:bold; color:black">${selectedSortOptionLabel}</span>`
          : `Sort By: ${selectedSortOptionLabel}`
      }
      isFilterEmpty={initialValue === value}
      variant={variant}
      withClear={false}
      dropdownAligning={dropdownAligning}
      widthInside={widthInside}
      isSelectionOptionBlack={isSelectionOptionBlack}
      filterSpacingClass={filterSpacingClass}
      fontSize={fontSize}
      filterMaxWidthClass="max-w-64"
      icon={<MdSort className="mr-1 mt-1 min-w-4" />}
    >
      {sortingValues.map((item) => (
        <Dropdown.Item key={item.value} label={item.label} value={item.value} variant="select">
          <button
            type="button"
            onClick={() => handleSortChange(item)}
            className="col-span-4 text-left p-1 focus:outline-none text-left"
          >
            {item.label}
          </button>
          <div className="place-self-end self-center">
            {item.value === value && <>{direction ? <FaCaretDown /> : <FaCaretUp />}</>}
          </div>
        </Dropdown.Item>
      ))}
    </Filter>
  );
};

SortingFilter.propTypes = {
  sortingValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ).isRequired,
  initialValue: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  direction: PropTypes.number.isRequired,
  onSortChange: PropTypes.func.isRequired,
  resultsLabel: PropTypes.string.isRequired,
  variant: PropTypes.string,
  widthInside: PropTypes.string,
  isSelectionOptionBlack: PropTypes.bool,
  filterSpacingClass: PropTypes.string,
  fontSize: PropTypes.string,
  dropdownAligning: PropTypes.string,
};

SortingFilter.defaultProps = {
  variant: 'transparentOutline',
  widthInside: '',
  isSelectionOptionBlack: undefined,
  filterSpacingClass: undefined,
  fontSize: undefined,
  dropdownAligning: 'right',
};

export default memo(SortingFilter);
